import { IImageStore } from '@af-create/ImageList/_stores/ImageStore';
import ImageWrapper from '@af-create/Webgl/ImageWrapper';
import * as Resources from '../../Resource/Resources';
import Pairing from '../../Common/Utils/Pairing';

export default class UserImageLibrary extends Resources.Lib<IImageStore> {
	protected chunk_map:{[id:string]: {[id:number]: number}} = {};

	public get = (id:string):Resources.Resource => this.map.hasOwnProperty(id) ? this.contents[this.map[id]] : null;

	public getChunk = (id: string, chunk_x: number, chunk_y: number) => {
		let chunk_id = Pairing.cantor(chunk_x, chunk_y);
		return this.map.hasOwnProperty(id) ? this.contents[this.chunk_map[id][chunk_id]] : null;
	}

	public flush = () => {
		this.contents = [];
		this.map = {};
	}

	public retrieve = (image:IImageStore, full:boolean = false):Promise<IImageStore> => {
		return this.retrieveImage(image, full);
	}

	public retrieveImage = (image:IImageStore, full: boolean):Promise<IImageStore> => {
		return new Promise((resolve, reject) => {
			let resource:Resources.Resource;
			
			if(this.map.hasOwnProperty(image.id)){
				resource = this.contents[this.map[image.id]];
			} else {
				let imagenode = document.createElement('img');
				imagenode.crossOrigin = "use-credentials";
				resource = {id:image.id, value: ImageWrapper.fromImage(imagenode), status: Resources.STATUS_NONE, extra: {
					width: image.width, 
					height: image.height,
					chunked: image.chunked,
				}}

				if (image.chunked) {
					let count_x = Math.ceil(image.width / 4096);
					let count_y = Math.ceil(image.height / 4096);
					for (let x = 0; x < count_x; x++) {
						for (let y = 0; y < count_y; y++) {
							this.retrieveChunk(image, x, y);
						}
					}
				}

				this.map[image.id] = this.contents.length;
				this.contents[this.map[image.id]] = resource;
			}

			resource.value.content.onload = () => {
				resource.status = Resources.STATUS_SUCCESS;
				resolve(image);
			};

			resource.value.content.onerror = () => {
				resource.status = Resources.STATUS_ERROR;
				reject("Error loading image " + resource.value.src);
			}

			switch(resource.status){
				case Resources.STATUS_NONE:
					resource.status = Resources.STATUS_FETCHING;
					resource.value.content.src = image.src;
					break;
				case Resources.STATUS_SUCCESS:
					resolve(image);
					break;
				case Resources.STATUS_ERROR:
					reject("Error loading image " + resource.value.content.src);
					break;
			}
		});
	}

	public retrieveChunk = (image:IImageStore, chunk_x: number, chunk_y: number) => {
		return new Promise((resolve, reject) => {
			let resource:Resources.Resource;
			let chunk_id = Pairing.cantor(chunk_x, chunk_y)
			
			if(this.map.hasOwnProperty(image.id)){
				resource = this.contents[this.chunk_map[image.id][chunk_id]];
			} else {
				let imagenode = document.createElement('img');
				imagenode.crossOrigin = "use-credentials";
				resource = {id:image.id, value: ImageWrapper.fromImage(imagenode), status: Resources.STATUS_NONE, extra: {
					chunked: image.chunked,
				}}
				if(!(image.id in this.chunk_map)){
					this.chunk_map[image.id] = []
				}
				this.chunk_map[image.id][chunk_id] = this.contents.length;
				this.contents[this.chunk_map[image.id][chunk_id]] = resource;
			}

			resource.value.content.onload = () => {
				resource.status = Resources.STATUS_SUCCESS;
				resource.extra['width'] = resource.value.content.width;
				resource.extra['height'] = resource.value.content.height;
				resolve(image);
			};

			resource.value.content.onerror = () => {
				resource.status = Resources.STATUS_ERROR;
				reject("Error loading image " + resource.value.src);
			}

			switch(resource.status){
				case Resources.STATUS_NONE:
					resource.status = Resources.STATUS_FETCHING;
					resource.value.content.src = image.src+'?x='+chunk_x+'&y='+chunk_y;
					break;
				case Resources.STATUS_SUCCESS:
					resolve(image);
					break;
				case Resources.STATUS_ERROR:
					reject("Error loading image " + resource.value.content.src);
					break;
			}
		});
	}
	
	public remove = (id:string):Promise<string> => {
		return new Promise((resolve, reject) => {
			this.contents[this.map[id]] = null;

			if(id in this.chunk_map){
				for(let chunk_id in this.chunk_map[id]) {
					this.contents[this.chunk_map[id][chunk_id]] = null;
				}
			}
			delete this.chunk_map[id]

			delete this.map[id];
			resolve(id);
		});
	}
}